import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase/config';
import {
  generateAndEncryptKeypair,
  decryptPrivateKey,
  verifyKeyPair,
  storePrivateKeyInSession,
  hasPrivateKeyInSession,
  clearPrivateKeyFromSession,
} from './services/cryptoKeyService';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import SetPassword from './components/SetPassword';
import AdminRequests from './components/AdminRequests';
import './App.css';

const AUDITORS_COLLECTION = 'auditors';

function App({ inviteSuccess = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [auditorData, setAuditorData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [keyStatus, setKeyStatus] = useState(null); // 'generating' | 'decrypting' | null

  // Temporarily hold the password between handleLogin and onAuthStateChanged
  const pendingPasswordRef = useRef(null);
  // Guard: avoid signing out when a duplicate onAuthStateChanged runs while key setup is still in progress
  const keySetupInProgressRef = useRef(false);

  // ── Auth state listener ──────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // 1. Verify user is an auditor
          let auditorDocData = null;
          let auditorDocId = null;

          const auditorDoc = await getDoc(doc(db, AUDITORS_COLLECTION, currentUser.uid));
          if (auditorDoc.exists()) {
            auditorDocData = auditorDoc.data();
            auditorDocId = auditorDoc.id;
          } else {
            // Fallback: look up by email
            const emailDoc = await getDoc(doc(db, AUDITORS_COLLECTION, currentUser.email));
            if (emailDoc.exists()) {
              auditorDocData = emailDoc.data();
              auditorDocId = emailDoc.id;
            }
          }

          if (!auditorDocData) {
            setAuthError('Access denied. You are not authorized as an auditor.');
            await signOut(auth);
            setUser(null);
            setAuditorData(null);
            setLoading(false);
            return;
          }

          // Invited auditors must set password via the invite link first
          const status = auditorDocData.status ?? 'active';
          if (status === 'invited') {
            setAuthError('Please use the invite link from your email to set your password and activate your account.');
            await signOut(auth);
            setUser(null);
            setAuditorData(null);
            setLoading(false);
            return;
          }

          const fullAuditorData = {
            id: auditorDocId,
            ...auditorDocData,
            uid: auditorDocData.uid || currentUser.uid,
            grantId: auditorDocData.uid || (auditorDocId.includes('@') ? auditorDocId.toLowerCase() : auditorDocId),
          };

          // Admin status comes from the `admin` custom claim (set via scripts/set-admin.js).
          try {
            const tokenResult = await currentUser.getIdTokenResult();
            setIsAdmin(tokenResult.claims.admin === true);
          } catch (e) {
            setIsAdmin(false);
          }

          // 2. Key management ─ only when we have the password (fresh login)
          const password = pendingPasswordRef.current;
          if (password) {
            pendingPasswordRef.current = null; // consume once
            keySetupInProgressRef.current = true; // block duplicate listener from signing out
            try {
              if (!auditorDocData.encPrivKey) {
                // ── First login: generate keys ──────────────────────
                setKeyStatus('generating');
                console.log('[App] First login — generating SECP256k1 keypair…');

                const keyData = await generateAndEncryptKeypair(password);

                // Persist public key + encrypted private key to Firestore
                const auditorRef = doc(db, AUDITORS_COLLECTION, auditorDocId);
                await updateDoc(auditorRef, {
                  publicKey: keyData.publicKey,
                  encPrivKey: keyData.encPrivKey,
                  salt: keyData.salt,
                  kdfParams: keyData.kdfParams,
                  iv: keyData.iv,
                  keyVersion: keyData.keyVersion,
                  updatedAt: new Date().toISOString(),
                });

                // Update local copy so Dashboard has the public key immediately
                fullAuditorData.publicKey = keyData.publicKey;
                fullAuditorData.encPrivKey = keyData.encPrivKey;
                fullAuditorData.salt = keyData.salt;
                fullAuditorData.kdfParams = keyData.kdfParams;
                fullAuditorData.iv = keyData.iv;
                fullAuditorData.keyVersion = keyData.keyVersion;

                // Verify the generated key matches before storing in session
                if (!verifyKeyPair(keyData.privateKeyHex, keyData.publicKey)) {
                  throw new Error('Generated keypair mismatch — this should not happen');
                }
                console.log('[App] Keys generated, verified, and stored successfully');
                storePrivateKeyInSession(keyData.privateKeyHex);
              } else {
                // ── Returning login: decrypt existing key ───────────
                setKeyStatus('decrypting');
                console.log('[App] Returning login — decrypting stored private key…');

                const privateKeyHex = await decryptPrivateKey(password, {
                  encPrivKey: auditorDocData.encPrivKey,
                  salt: auditorDocData.salt,
                  kdfParams: auditorDocData.kdfParams,
                  iv: auditorDocData.iv,
                });

                // Verify decrypted private key matches the stored public key
                if (!verifyKeyPair(privateKeyHex, auditorDocData.publicKey)) {
                  throw new Error(
                    'Decrypted private key does NOT match stored public key. ' +
                    'Key data may be corrupted or password changed.'
                  );
                }
                console.log('[App] Private key decrypted and verified against stored public key');
                storePrivateKeyInSession(privateKeyHex);
              }
              setKeyStatus(null);
              keySetupInProgressRef.current = false;
            } catch (keyError) {
              console.error('[App] Key management error:', keyError);
              setKeyStatus(null);
              keySetupInProgressRef.current = false;
              setAuthError(`Key management failed: ${keyError.message}`);
              clearPrivateKeyFromSession();
              await signOut(auth);
              setUser(null);
              setAuditorData(null);
              setLoading(false);
              return;
            }
          } else {
            // No password available (page reload within same tab, or new tab, or duplicate listener)
            if (!hasPrivateKeyInSession()) {
              // Key setup may still be in progress from a previous listener run (race with onAuthStateChanged)
              if (keySetupInProgressRef.current) {
                setLoading(false);
                return;
              }
              // Session key is missing in THIS tab only. Do not sign the user out globally.
              // Keep them logged in so other tabs remain authenticated; decrypt operations
              // will detect the missing session key and prompt for re-auth as needed.
              console.log('[App] No session key available in this tab — keeping auth session; decryption will require re-auth.');
            }
            // Session key exists (same-tab reload) — proceed normally
          }

          setUser(currentUser);
          setAuditorData(fullAuditorData);
          setAuthError(null);
          if (typeof window !== 'undefined' && window.location.search.includes('invite=success')) {
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Error verifying auditor:', error);
          setAuthError('Error verifying auditor access. Please try again.');
          await signOut(auth);
          setUser(null);
          setAuditorData(null);
        }
      } else {
        setUser(null);
        setAuditorData(null);
        setIsAdmin(false);
        setAuthError(null);
        clearPrivateKeyFromSession();
        keySetupInProgressRef.current = false; // reset so next login is not stuck
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // ── Login handler ────────────────────────────────────────────
  const handleLogin = async (email, password) => {
    try {
      setAuthError(null);
      // Stash the password so onAuthStateChanged can use it for key management
      pendingPasswordRef.current = password;
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged fires next and handles key management
    } catch (error) {
      pendingPasswordRef.current = null; // clear on failure
      console.error('Login error:', error.code, error.message);

      let errorMessage = 'Failed to log in. Please check your credentials.';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email/password authentication is not enabled. Please contact an administrator.';
      } else if (error.code) {
        errorMessage = `Login failed: ${error.code}. Please check your credentials.`;
      }
      throw new Error(errorMessage);
    }
  };

  // ── Logout handler ───────────────────────────────────────────
  const handleLogout = async () => {
    try {
      clearPrivateKeyFromSession();
      await signOut(auth);
      setAuthError(null);
      // Clear invite=success from URL so the "Password set" message is not shown again
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleKeyMaterialUpdated = (updated) => {
    setAuditorData((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  const onAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (user && auditorData && onAdminRoute && !isAdmin) {
      navigate('/', { replace: true });
    }
  }, [user, auditorData, onAdminRoute, isAdmin, navigate]);

  // ── Render ───────────────────────────────────────────────────
  if (loading || keyStatus) {
    return (
      <div className="app-loading" role="status" aria-live="polite" aria-busy="true">
        <div className="loading-spinner" aria-hidden="true"></div>
        <p>
          {keyStatus === 'generating'
            ? 'Generating your encryption keys…'
            : keyStatus === 'decrypting'
              ? 'Unlocking your encryption keys…'
              : 'Loading…'}
        </p>
      </div>
    );
  }

  return (
    <div className="app">
      {user && auditorData ? (
        onAdminRoute && isAdmin ? (
          <AdminRequests
            user={user}
            auditorData={auditorData}
            onLogout={handleLogout}
            onKeyMaterialUpdated={handleKeyMaterialUpdated}
          />
        ) : onAdminRoute && !isAdmin ? (
          <div className="app-loading" role="status">
            <p>Redirecting to dashboard…</p>
          </div>
        ) : (
          <Dashboard
            user={user}
            auditorData={auditorData}
            onLogout={handleLogout}
            isAdmin={isAdmin}
            onKeyMaterialUpdated={handleKeyMaterialUpdated}
          />
        )
      ) : (
        <Login onLogin={handleLogin} error={authError} inviteSuccess={inviteSuccess} />
      )}
    </div>
  );
}

function AppWithRouter() {
  const location = useLocation();
  const inviteSuccess = location.search.includes('invite=success');

  return (
    <App inviteSuccess={inviteSuccess} />
  );
}

function AppRoot() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/invite/:token" element={<SetPassword />} />
        <Route path="*" element={<AppWithRouter />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRoot;
